import { useEffect, useRef } from "react";
import type { useZero } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import { mutators } from "@/lib/mutators";

interface UseMarkChannelReadArgs {
  channelId: string | null;
  hidden: boolean;
  user: { id: string } | null;
  latestMessageCreatedAt: number;
  lastReadMap: Map<string, number | null>;
  zero: ReturnType<typeof useZero>;
}

/**
 * Marks the active channel as read whenever a newer message arrives —
 * via `actions.markChannelRead` (which fans out to
 * `user_channel_stats.last_read_at` in a server-mutator extension) —
 * and marks the corresponding notification events as read so the
 * unread badge clears across devices.
 *
 * Gated on `hidden`: this MessageArea instance might be in the
 * multiplex kept-alive set but the user isn't looking at it. Without
 * the guard, a new message arriving on a background channel would
 * silently mark it as read, clearing the user's unread badge for a
 * channel they never returned to.
 */
export function useMarkChannelRead({
  channelId,
  hidden,
  user,
  latestMessageCreatedAt,
  lastReadMap,
  zero,
}: UseMarkChannelReadArgs) {
  const pendingMarkRef = useRef<{
    channelId: string;
    lastReadAt: number;
  } | null>(null);

  useEffect(() => {
    if (hidden) return;
    if (!channelId || !user || latestMessageCreatedAt === 0) return;
    const targetLastReadAt = latestMessageCreatedAt;
    const lastReadAt = lastReadMap.get(channelId);
    if ((lastReadAt ?? 0) >= targetLastReadAt) {
      const pending = pendingMarkRef.current;
      if (
        pending?.channelId === channelId &&
        pending.lastReadAt <= targetLastReadAt
      ) {
        pendingMarkRef.current = null;
      }
      return;
    }

    const pending = pendingMarkRef.current;
    if (
      pending?.channelId === channelId &&
      pending.lastReadAt >= targetLastReadAt
    ) {
      return;
    }
    pendingMarkRef.current = { channelId, lastReadAt: targetLastReadAt };

    (async () => {
      try {
        const member = await zero.run(
          queries.channel_members.byChannelAndUser({
            channelId,
            userId: user.id,
          }),
        );
        // Read + write both go through user_channel_stats: lastReadMap
        // is sourced from `ucs.last_read_at` (selectors/memberships.ts)
        // and `actions.markChannelRead`'s server-mutator extension
        // writes ucs via raw SQL.
        if (member && (lastReadAt ?? 0) < targetLastReadAt) {
          await zero.mutate(
            mutators.actions.markChannelRead({
              membershipId: member.id,
              lastReadAt: targetLastReadAt,
            }),
          );
          // Mark notification events as read (syncs cross-device)
          void zero.mutate(
            mutators.actions.markChannelNotificationsRead({
              userId: user.id,
              channelId,
            }),
          );
          return;
        }
        pendingMarkRef.current = null;
      } catch {
        const pending = pendingMarkRef.current;
        if (
          pending?.channelId === channelId &&
          pending.lastReadAt === targetLastReadAt
        ) {
          pendingMarkRef.current = null;
        }
        // Non-critical — don't block channel viewing
      }
    })();
  }, [channelId, hidden, lastReadMap, latestMessageCreatedAt, user, zero]);
}
